function getRelevantSources(entry) {
  return Array.isArray(entry?.rights?.sources)
    ? entry.rights.sources.filter((source) => source?.name)
    : [];
}

function formatSeries(values = []) {
  if (values.length === 0) {
    return "";
  }

  if (values.length === 1) {
    return values[0];
  }

  if (values.length === 2) {
    return `${values[0]} and ${values[1]}`;
  }

  return `${values.slice(0, -1).join(", ")}, and ${values.at(-1)}`;
}

function renderExternalLink(label, url) {
  if (!url) {
    return label;
  }

  return (
    <a href={url} target="_blank" rel="noreferrer">
      {label}
    </a>
  );
}

function InstrumentAdaptationsSection({
  instrumentSource,
  adaptationSources
}) {
  if (!instrumentSource && adaptationSources.length === 0) {
    return null;
  }

  const instrumentName =
    instrumentSource?.name ||
    instrumentSource?.sourceTitle ||
    "This instrument";
  const rightsHolder =
    instrumentSource?.rightsHolder || "";
  const isASHAInstrument =
    rightsHolder.includes("ASHA");
  const isCAPEVrInstrument =
    instrumentSource?.name === "CAPE-Vr";
  const isGRBASMethodology =
    instrumentSource?.name === "GRBAS";
  const heading =
    adaptationSources.length > 0
      ? "Instrument & adaptations"
      : "Instrument";

  let body =
    `${instrumentName} is used in All-Voiced according to the applicable source permissions recorded in the catalog.`;

  if (isASHAInstrument) {
    body =
      "All-Voiced uses the Consensus Auditory-Perceptual Evaluation of Voice (CAPE-V) with permission from ASHA.";
  } else if (isCAPEVrInstrument) {
    body =
      "All-Voiced uses the Consensus Auditory-Perceptual Evaluation of Voice—Revised (CAPE-Vr), described by Kempster, Nagle, and Solomon (2025) and licensed under CC BY 4.0.";
  } else if (isGRBASMethodology) {
    body =
      "GRBAS is based on the original methodological source described by Hirano (1981).";
  }

  return (
    <div className="catalog-detail__source-section">
      <h3>{heading}</h3>
      <p>
        {body}{" "}
        {adaptationSources.length > 0
          ? "Available language adaptations are based on their applicable published sources and permissions. "
          : ""}
      </p>
    </div>
  );
}

function VoiceSamplesSection({
  datasetSource,
  sampleSetSource
}) {
  if (!datasetSource && !sampleSetSource) {
    return null;
  }

  const isPvqdDataset =
    datasetSource?.name === "PVQD" ||
    datasetSource?.sourceTitle ===
      "Perceptual Voice Qualities Database (PVQD)";
  const datasetLink = datasetSource?.sourceUrl || null;
  const licenseName =
    datasetSource?.license?.name || "";
  const licenseSuffix = licenseName.includes("CC BY 4.0")
    ? ", licensed under CC BY 4.0."
    : licenseName
      ? `, licensed under ${licenseName}.`
      : ".";
  const isDiagnosisSubset =
    sampleSetSource?.name === "PVQD (Diagnosis Subset)";

  return (
    <div className="catalog-detail__source-section">
      <h3>Voice samples</h3>
      <p>
        {isDiagnosisSubset
          ? "Guess the Diagnosis uses the All-Voiced curated diagnosis sample set"
          : sampleSetSource
            ? "Sample-based practice uses the All-Voiced curated sample set"
            : "Sample-based practice uses voice samples"}{" "}
        {datasetSource
          ? (
            <>
              derived from{" "}
              {isPvqdDataset
                ? renderExternalLink(
                    "Walden’s Perceptual Voice Qualities Database (PVQD)",
                    datasetLink
                  )
                : renderExternalLink(
                    datasetSource?.sourceTitle || datasetSource?.name,
                    datasetLink
                  )}{" "}
              {isPvqdDataset
                ? <>Mendeley Data, V4 (2022)</>
                : <>and the catalog-recorded source dataset</>}
              {licenseSuffix}
            </>
          )
          : <>used by this activity.</>}
        {isDiagnosisSubset
          ? " These samples were selected because diagnosis information is available for the game."
          : ""}
      </p>
    </div>
  );
}

function DiagnosisInformationSection({
  datasetSource
}) {
  const appliesToDiagnosisInformation =
    datasetSource?.applicability?.notes?.toLowerCase().includes("diagnosis information");

  if (!appliesToDiagnosisInformation) {
    return null;
  }

  return (
    <div className="catalog-detail__source-section">
      <h3>Diagnosis information</h3>
      <p>
        The game checks responses against diagnosis information associated with
        the source PVQD samples. All-Voiced presents this information through
        the Guess the Diagnosis activity; PVQD is the source of the underlying
        sample metadata, not the game workflow.
      </p>
    </div>
  );
}

function ReferenceFeedbackSection({
  referenceSources,
  entry
}) {
  if (referenceSources.length === 0) {
    return null;
  }

  const pvqdReferenceSource =
    referenceSources.find(
      (source) =>
        source?.name === "PVQD reference evaluations" ||
        source?.sourceType === "externalDataset"
    ) || null;
  const sharedEvaluatorReferenceSource =
    referenceSources.find(
      (source) =>
        source !== pvqdReferenceSource &&
        [
          "internalMaterial",
          "allVoicedOriginal"
        ].includes(source?.sourceType)
    ) || null;
  const usesDerivedSharedReferenceValues =
    referenceSources.some((source) =>
      /median|derived|aggregated/i.test(
        `${source?.name || ""} ${source?.sourceTitle || ""} ${source?.notes || ""}`
      )
    );

  let leadSentence = "Reference feedback is based on the source material recorded for this activity.";

  if (pvqdReferenceSource && sharedEvaluatorReferenceSource) {
    leadSentence =
      "For sample-based practice, feedback may draw on sample-linked reference evaluations from PVQD and on evaluations shared for feedback within current All-Voiced workflows.";
  } else if (pvqdReferenceSource) {
    leadSentence = entry?.slug === "grbas"
      ? "For sample-based practice, feedback draws on sample-linked reference evaluations from PVQD."
      : "For sample-based practice, feedback may draw on sample-linked reference evaluations from PVQD.";
  } else if (sharedEvaluatorReferenceSource) {
    leadSentence =
      usesDerivedSharedReferenceValues
        ? "For sample-based practice, feedback may draw on reference values derived from evaluations shared for feedback within current All-Voiced workflows."
        : "For sample-based practice, feedback may draw on evaluations shared for feedback within current All-Voiced workflows.";
  }

  return (
    <div className="catalog-detail__source-section">
      <h3>Reference feedback</h3>
      <p>
        {leadSentence} These provide reference feedback rather than a single
        definitive rating.
      </p>
    </div>
  );
}

function buildSectionIntro({
  instrumentSource,
  adaptationSources,
  datasetSource,
  sampleSetSource
}) {
  const referenceTargets = [];

  if (instrumentSource) {
    referenceTargets.push(
      instrumentSource?.name === "GRBAS"
        ? "original methodological source"
        : "original instrument"
    );
  }

  if (adaptationSources.length > 0) {
    referenceTargets.push("selected adaptation");
  }

  if (datasetSource) {
    referenceTargets.push("voice database");
  }

  if (sampleSetSource) {
    referenceTargets.push(
      sampleSetSource?.name === "PVQD (Diagnosis Subset)"
        ? "diagnosis sample collection"
        : "sample collection"
    );
  }

  if (referenceTargets.length === 0) {
    return null;
  }

  return `Full references for the ${formatSeries(referenceTargets)} are available within the activity.`;
}

function FallbackAttributionSection({
  publicAttributionText,
  sources
}) {
  if (!publicAttributionText && sources.length === 0) {
    return null;
  }

  return (
    <>
      {publicAttributionText ? <p>{publicAttributionText}</p> : null}

      {sources.length > 0 ? (
        <ul className="catalog-detail__sources-list">
          {sources.map((source) => (
            <li key={`${source.category}-${source.name}`}>
              {source.sourceUrl
                ? renderExternalLink(source.name, source.sourceUrl)
                : <span>{source.name}</span>}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

export default function CatalogEntrySourcesAttributionPanel({
  entry
}) {
  const publicAttributionText =
    entry?.rights?.publicAttributionText;
  const sources = getRelevantSources(entry);

  const instrumentSource =
    sources.find((source) => source.category === "instrument") ||
    null;
  const adaptationSources =
    sources.filter(
      (source) => source.category === "instrumentAdaptation"
    );
  const datasetSource =
    sources.find(
      (source) => source.category === "voiceSampleDataset"
    ) || null;
  const sampleSetSource =
    sources.find(
      (source) => source.category === "voiceSampleSet"
    ) || null;
  const referenceSources =
    sources.filter(
      (source) => source.category === "referenceRatings"
    );
  const sectionIntro = buildSectionIntro({
    instrumentSource,
    adaptationSources,
    datasetSource,
    sampleSetSource
  });

  const hasStructuredSections = Boolean(
    instrumentSource ||
      adaptationSources.length ||
      datasetSource ||
      sampleSetSource ||
      referenceSources.length
  );

  if (!hasStructuredSections && !publicAttributionText) {
    return null;
  }

  return (
    <section className="catalog-detail__panel catalog-detail__panel--full-width">
      <h2>Sources &amp; references</h2>

      {sectionIntro ? (
        <p className="catalog-detail__section-intro">
          {sectionIntro}
        </p>
      ) : null}

      {hasStructuredSections ? (
        <div className="catalog-detail__source-sections">
          <InstrumentAdaptationsSection
            instrumentSource={instrumentSource}
            adaptationSources={adaptationSources}
          />
          <VoiceSamplesSection
            datasetSource={datasetSource}
            sampleSetSource={sampleSetSource}
          />
          <DiagnosisInformationSection
            datasetSource={datasetSource}
          />
          <ReferenceFeedbackSection
            referenceSources={referenceSources}
            entry={entry}
          />
        </div>
      ) : (
        <FallbackAttributionSection
          publicAttributionText={publicAttributionText}
          sources={sources}
        />
      )}
    </section>
  );
}
